@if (showSuggestedPayerModal) {
  <div>
    <client-bv-error 
      [data]="suggestedPayerData"
      [isMobile]="isMobile"
      (primaryAction)="onPrimaryAction()"
      (secondaryAction)="onSecondaryAction()"
      (tertiaryAction)="onTertiaryAction()">
    </client-bv-error>
  </div>
}

@if (showDemographicUpdateModal) {
  <div>
    <client-demographic-update
      [insuranceResponse]="demographicUpdateResponse"
      [isMobile]="isMobile"
      [useInline]="useInline"
      [providerNetworkCompany]="insuranceForm.form.value.provider_network_company"
      [userFormValues]="insuranceForm.form.value"
      (closeUpdate)="onDemographicUpdateClosed($event)"
      (hideParentContent)="hideParentContent.emit($event)">
    </client-demographic-update>
  </div>
}

@if (bvError) {
  <div>
    <span class="scroll-anchor" #bvErrorBannerContainer></span>
    <sonder-success-error-banner [isSuccess]="false" [message]="bvError"></sonder-success-error-banner>
  </div>
}


@if (processingNextStep) {
  <div class="processing-overlay">
    <div class="processing-content">
      <mat-spinner diameter="60"></mat-spinner>
    </div>
  </div>
}


@if (processingSelfPay) {
  <div class="processing-overlay">
    <div class="processing-content">
      <mat-spinner diameter="60"></mat-spinner>
    </div>
  </div>
}

@if (showComponent && !showSuggestedPayerModal && !showDemographicUpdateModal && !processingNextStep && !processingSelfPay) {
  <form #form="ngForm" (ngSubmit)="submit()">
    <sonder-form-body>
      @if (insuranceForm?.form) {
        <ng-container [formGroup]="insuranceForm.form">
          @if (useInline) {
            <sonder-form-legend
              [inline]="true"
              class="insurance-form-field-full"
            >
              <h3 class="section-label" data-test="insurance-confirm-section-label-text">Primary insurance details</h3>
              <p class="iris-body-responsive">
                If you also have a secondary plan, you can add it later under Account.
              </p>
            </sonder-form-legend>
          }

      <div class="insurance-form-field-full">
        <iris-form-field
          class="insurance-form-field-full"
          label="State where you will receive care"
          data-test="insurance-state-dropdown-header-text"
        >
          <iris-dropdown
            formControlName="insurance_state"
            [options]="usStateOption"
            [placeholder]="statesLoading ? 'Loading states...' : 'Select State'"
            data-test="insurance-state-dropdown-options"
          ></iris-dropdown>
        </iris-form-field>
        
        <!-- State selection help tooltip -->
        <div class="insurance-state-help-container">
          <span
            class="state-help-item"
            sonderPopupTrigger
            [popup]="differentStateHelp"
          >
            <mat-icon
              class="provider-id-icon"
              aria-label="insurance-different-state-help"
              color="primary"
            >info</mat-icon>&nbsp;
            <a class="iris-link-responsive">My insurance is in a different state</a>
            <sonder-popup #differentStateHelp [closeOnClick]="true" [offsetY]="-32">
              Select the state where you will receive care, which is usually the state you live in. 
              Most out of state insurers (like Blue Cross Blue Shield and Anthem) 
              require claims be submitted to the state of service.
            </sonder-popup>
          </span>
        </div>
      </div>

      <div class="insurance-form-field-full">
        <iris-form-field
          class="insurance-form-field-full"
          label="Insurance Provider"
          data-test="insurance-provider-dropdown-header-text"
        >
          <iris-dropdown
            placeholder="Select an Insurance Provider"
            formControlName="provider_network_company"
            [options]="(payerList$ | async) ?? []"
            optionDataTestAttributeName="name"
            data-test="insurance-provider-dropdown-state"
          ></iris-dropdown>
        </iris-form-field>

        <span
          class="state-provider-help-container"
          sonderPopupTrigger
          [popup]="providerHelp"
        >
          <mat-icon
            class="provider-id-icon"
            aria-label="state-provider-help"
            color="primary"
          >info</mat-icon>&nbsp;
          <a class="iris-link-responsive" data-test="insurance-confirm-paragraph-text">I don't see my insurance state or provider</a>
          <sonder-popup #providerHelp [closeOnClick]="true" [offsetY]="-32">
            With the exception of some Anthem, CareFirst, and BlueCross
            BlueShield companies, we only accept insurance plans from the state where you're located.
            If you don't see your insurance provider listed when you filter by your state,
            unfortunately we do not accept your insurance at this time.
            <br>
            <br>
            We know insurance can be complicated. Call us at
            <a href="tel:{{ supportPhoneLink }}">{{ supportPhoneDisplay }}</a>
            if you need help.
          </sonder-popup>
        </span>
      </div>

      <div class="group-number-member-id-row">
        <div class="group-number-checkbox-container">
          <iris-form-field
            label="Group Number"
            data-test="insurance-group-number-header-text"
          >
            <iris-text-input
              formControlName="group_number"
              pattern="[A-Za-z0-9\-]+"
              data-test="group-number-input"
            ></iris-text-input>
          </iris-form-field>
          <iris-checkbox formControlName="has_no_group_number">
            I don't have a Group number
          </iris-checkbox>
        </div>

        <iris-form-field
          [label]="memberIdLabel()"
          [helperText]="memberIdHelperText()"
          [customErrorMessages]="{
            pattern: 'Only letters, numbers, hyphens, and spaces are allowed.',
            minlength: 'Must be at least 2 characters.'
          }"
          data-test="insurance-memberID-number-header-text"
        >
          <iris-text-input
            formControlName="policy_number"
            class="provider-id-input"
            pattern="[A-Za-z0-9\- ]+"
            data-test="member-number-input"
          >
            <mat-icon
              class="provider-id-icon"
              sonderPopupTrigger
              [popup]="numberHelp"
              aria-label="member-number-help"
              color="primary"
            >info</mat-icon>
            <sonder-popup #numberHelp [closeOnClick]="true" [offsetY]="-32">
              Group and member/ID numbers can be any combination of letters,
              numbers, dashes, or spaces, and must be at least 2 characters.
              <br>
              <br>
              For Kaiser Permanente, this might also be called your
              Medical Record Number or Health Record Number.
            </sonder-popup>
          </iris-text-input>
        </iris-form-field>
      </div>

      <mat-error *ngIf="insuranceForm?.form.controls.policy_number?.errors?.policyOrGroupNotUnique || insuranceForm?.form.controls.group_number?.errors?.policyOrGroupNotUnique" class="group-member-id-error">
        Group and Member ID numbers should not match. If you don't know the group number, input 1000.
      </mat-error>

          @if (showAuthNumberField) {
            <iris-form-field
              class="insurance-form-field-full buffer-bottom"
              label="Authorization Number"
              [customErrorMessages]="{
                pattern: 'Authorization number must be exactly 9 digits',
                authNotUnique: 'Your authorization number is nine numerical digits and cannot be the same as your group or membership number.'
              }"
            >
              <iris-text-input
                formControlName="auth_number"
                data-test="field-auth-number"
                maxLength="9"
              >
              </iris-text-input>
            </iris-form-field>
          }

      <sonder-form-legend
        [inline]="true"
        class="insurance-form-field-full"
      >
        <h3 class="section-label" data-test="insurance-header-text">Add insurance card image</h3>
        <p class="iris-body-responsive" data-test="insurance-section-label-text">
          This will help us resolve any issues with your insurance or claims.
        </p>
      </sonder-form-legend>

      <sonder-image-upload
        id="pendo-front-insurance"
        sonderMediaqueryClass="mq"
        [ngClass]="{'front-insurance-card-full': !isMobile && !useInSessionRequest, 'insurance-image-upload-full' : useInSessionRequest, 'insurance-form-field-third' : !useInSessionRequest}"
        label="Card front"
        formControlName="front_card_uri"
        data-test="insurance-card-upload-front-header-text"
      ></sonder-image-upload>

      <sonder-image-upload
        id="pendo-back-insurance"
        sonderMediaqueryClass="mq"
        [ngClass]="{'insurance-image-upload-full' : useInSessionRequest, 'insurance-form-field-third' : !useInSessionRequest}"
        label="Card back"
        formControlName="back_card_uri"
        data-test="insurance-card-upload-back-header-text"
      ></sonder-image-upload>

      <sonder-form-legend [inline]="true">
        <h3 class="section-label" data-test="insurance-policy-holder-header-text">Plan policyholder</h3>
        <p class="iris-body-responsive" data-test="insurance-policy-holder-paragraph-text">
          You may be the owner of your insurance policy,
          or you might be insured through someone else's policy.
          If that's the case,
          they're the policyholder—be sure to enter their information.
        </p>
      </sonder-form-legend>

      <iris-form-field
        label="Policyholder"
        class="insurance-form-field-full"
        data-test="insurance-policy-dropdown-holder-header-text"
      >
        <iris-dropdown
          [options]="relationshipListOption"
          formControlName="policy_holder_relationship_id"
          placeholder="Select Relationship"
          data-test="policyholder-dropdown-selection"
        ></iris-dropdown>
      </iris-form-field>

      <div
        class="name-row"
        [ngClass]="{'thirds' : !useInSessionRequest}"
      >
        <iris-form-field
          
          label="First Name"
          data-test="insurance-policy-holder-firstname-header-text"
        >
          <iris-text-input formControlName="first_name" data-test="policyholder-firstname-input"></iris-text-input>
        </iris-form-field>

        <iris-form-field
          label="Last Name"
          data-test="insurance-policy-holder-lastname-header-text"
        >
          <iris-text-input formControlName="last_name" data-test="policyholder-lastname-input"></iris-text-input>
        </iris-form-field>

        <iris-form-field
          label="Birthday"
          data-test="insurance-policy-holder-birthday-header-text"
          [customErrorMessages]="{
            mask: 'Please enter a valid date in mm/dd/yyyy format'
          }"
        >
          <iris-text-input
            type="text"
            formControlName="date_of_birth"
            data-test="policyholder-birthday-input"
            [mask]="masks.birthdayMask.ngxMask"
            [dropSpecialCharacters]="false"
            placeholder="mm/dd/yyyy"
          ></iris-text-input>
        </iris-form-field>
      </div>

      <!-- Message when a client selects coverage insurance as their insurance network. -->
          @if (showCoverageMessage) {
            <sonder-form-legend [inline]="true">
              <p class="iris-body-responsive">
                You've entered information for a Medicare Advantage insurance plan—if
                you have other insurance coverage through your spouse or partner,
                enter that information under "Account" after you complete your profile.
                Please note that we can't accept supplemental insurance (like AARP).
              </p>
            </sonder-form-legend>
          }
        </ng-container>
      }
    </sonder-form-body>

    @if (!useInline) {
      <iris-button-container
        [alignment]="IrisButtonContainerAlignments.END"
      >
        <iris-button
          id="pendo-insurance-save"
          data-test="insurance-save-button"
          [typeAttribute]="IrisButtonTypeAttributeOptions.SUBMIT"
          [disabled]="checkingWithInsurance || submitting || insuranceForm.invalid"
        >
          @if (!submitting) {
            <span>
              Save
            </span>
          } @else {
            @if (!chcHasAnOutageFeatureFlag) {
              <mat-spinner
                diameter="24"
                aria-label="Submission is processing"
              ></mat-spinner>
            }

            @if (chcHasAnOutageFeatureFlag) {
              <div class="button-loading-with-message">
                <mat-spinner diameter="24" aria-label="Submission is processing"></mat-spinner>
                <div class="loading-message">Submission is processing</div>
              </div>
            }
          }
        </iris-button>
      </iris-button-container>
    }
  </form>
}
